import { Observable } from 'rxjs';
import { Message } from '../models';

/**
 * Response from creating a new chat conversation
 * POST /api/v1/chat
 */
export interface NewChatResponse {
  id: string;
  createdAt: string;
}

/**
 * Configuration options for creating a chat
 */
export interface ChatConfig {
  configuration?: string;
  origin?: string;
  instance?: string;
}

/**
 * Abstract interface for chat providers
 * Can be implemented by WebSocket, REST, or other communication protocols
 */
export abstract class ChatProvider {
  /**
   * Observable stream of received messages
   * Components subscribe to this to receive messages from the bot
   */
  abstract receivedMessage(): Observable<Message>;

  /**
   * Create a new chat conversation
   * @param config Configuration for the chat (branch, instance, origin)
   * @returns Observable with conversation ID and creation timestamp
   */
  abstract createChat(config?: ChatConfig): Observable<NewChatResponse>;

  /**
   * Send a message to an existing conversation
   * The response (bot message) will be emitted through receivedMessage()
   * @param conversationId The conversation ID
   * @param message The message content
   * @returns Observable that completes when message is sent
   */
  abstract sendMessage(
    conversationId: string,
    message: string
  ): Observable<void>;

  /**
   * Send a conclusion message when the user closes the conversation
   * This message is appended to the conversation history but never displayed in the UI
   * @param conversationId The conversation ID
   * @returns Observable that completes when the message is sent
   */
  abstract sendConclusionMessage(
    conversationId: string
  ): Observable<void>;

  /**
   * Send a conclusion message when the conversation is closed due to inactivity timeout
   * This message is appended to the conversation history but never displayed in the UI
   * @param conversationId The conversation ID
   * @returns Observable that completes when the message is sent
   */
  abstract sendTimeoutMessage(
    conversationId: string
  ): Observable<void>;
}
